import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import api, { voucherAPI } from '../services/api';
import { 
  Printer, 
  RotateCcw, 
  Search, 
  Wifi, 
  ChevronDown,
  FileText,
  Ticket,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader,
  ArrowLeft,
  Users
} from 'lucide-react';

const VouchersAdminPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('imprimir');
  const [loading, setLoading] = useState(false);
  
  // Estados para selección inicial
  const [generationType, setGenerationType] = useState(''); // 'admin' o 'seller'
  const [showTypeSelection, setShowTypeSelection] = useState(true);
  
  // Pre-selección desde navegación (botón en vendedores)
  const preselectedData = location.state;
  
  // Estados para Imprimir Fichas
  const [routers, setRouters] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [plans, setPlans] = useState([]); // Planes del admin para generación propia
  const [selectedRouter, setSelectedRouter] = useState('');
  const [selectedSeller, setSelectedSeller] = useState('');
  const [sellerPlans, setSellerPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [printerType, setPrinterType] = useState('normal'); // normal o thermal
  const [pdfSize, setPdfSize] = useState('a4'); // a4 o ticket
  const [cardsPerPage, setCardsPerPage] = useState(24); // máximo 24 por hoja A4
  const [lastPdfUrl, setLastPdfUrl] = useState(null); // enlace al último PDF generado
  const [quantity, setQuantity] = useState(10);
  const [generating, setGenerating] = useState(false);
  
  // Estados para Reimprimir
  const [recentBatches, setRecentBatches] = useState([]);
  const [batchLimit, setBatchLimit] = useState(20);
  const [verifying, setVerifying] = useState(false);
  
  // Estados para verificación y regeneración
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [currentBatchAction, setCurrentBatchAction] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  
  // Estados para Buscar Ficha
  const [searchCode, setSearchCode] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  
  // Estados para IP Bindings
  const [selectedRouterBinding, setSelectedRouterBinding] = useState('');
  const [macAddress, setMacAddress] = useState('');
  const [bindingComment, setBindingComment] = useState('');
  const [creatingBinding, setCreatingBinding] = useState(false);

  useEffect(() => {
    loadRouters();
    loadPlans();
    if (activeTab === 'reimprimir') {
      loadRecentBatches();
    }
  }, [activeTab]);

  // Pre-seleccionar seller si viene desde navegación
  useEffect(() => {
    const preloadSellerData = async () => {
      if (preselectedData?.generationType === 'seller' && preselectedData?.preselectedSeller) {
        try {
          // Obtener información del vendedor para pre-cargar su router
          const sellerResponse = await api.get(`/api/sellers/${preselectedData.preselectedSeller}`);
          const seller = sellerResponse.data;
          
          // Pre-seleccionar tipo, router y vendedor
          setGenerationType('seller');
          setShowTypeSelection(false);
          setSelectedRouter(seller.router_id);  // Pre-seleccionar router del vendedor
          
          // Esperar a que se carguen los vendedores del router y luego seleccionar
          setTimeout(() => {
            setSelectedSeller(preselectedData.preselectedSeller);
          }, 500);
        } catch (error) {
          console.error('Error pre-cargando datos del vendedor:', error);
        }
      }
    };
    
    preloadSellerData();
  }, [preselectedData]);


  useEffect(() => {
    if (selectedRouter) {
      loadSellers(selectedRouter);
    }
  }, [selectedRouter]);

  useEffect(() => {
    if (selectedSeller) {
      loadSellerPlans(selectedSeller);
    }
  }, [selectedSeller]);

  const loadRouters = async () => {
    try {
      const response = await api.get('/api/routers');
      setRouters(response.data);
    } catch (error) {
      console.error('Error cargando routers:', error);
    }
  };

  const loadPlans = async () => {
    try {
      const response = await api.get('/api/plans');
      setPlans(response.data);
    } catch (error) {
      console.error('Error cargando planes:', error);
    }
  };

  const loadSellers = async (routerId) => {
    try {
      const response = await api.get(`/api/sellers?router_id=${routerId}`);
      setSellers(response.data);
      setSelectedSeller('');
      setSellerPlans([]);
    } catch (error) {
      console.error('Error cargando vendedores:', error);
    }
  };

  const loadSellerPlans = async (sellerId) => {
    try {
      // Obtener vendedor
      const sellerResponse = await api.get(`/api/sellers/${sellerId}`);
      const seller = sellerResponse.data;
      
      // Obtener punto de venta
      const posResponse = await api.get(`/api/points-of-sale/${seller.pos_id}`);
      const pointOfSale = posResponse.data;
      
      // Los planes del vendedor son los del punto de venta
      setSellerPlans(pointOfSale.assigned_plans || []);
      setSelectedPlan('');
    } catch (error) {
      console.error('Error cargando planes del vendedor:', error);
      setSellerPlans([]);
    }
  };

  const handleGenerateVouchers = async () => {
    // Validaciones según tipo de generación
    if (generationType === 'seller') {
      if (!selectedRouter || !selectedSeller || !selectedPlan || quantity < 1) {
        alert('Por favor completa todos los campos');
        return;
      }
    } else if (generationType === 'admin') {
      if (!selectedRouter || !selectedPlan || quantity < 1) {
        alert('Por favor completa todos los campos');
        return;
      }
    }

    setGenerating(true);
    try {
      let response;
      
      if (generationType === 'seller') {
        // Generar fichas para vendedor (afecta saldo)
        response = await api.post('/api/admin/vouchers/generate', {
          router_id: selectedRouter,
          seller_id: selectedSeller,
          plan_id: selectedPlan,
          quantity: parseInt(quantity),
          printer_type: printerType
        });
      } else {
        // Generar fichas para admin (NO afecta saldo)
        response = await api.post('/api/admin/vouchers/generate-for-admin', {
          router_id: selectedRouter,
          plan_id: selectedPlan,
          quantity: parseInt(quantity),
          printer_type: printerType
        });
      }

      if (response.data.success) {
        // Construir mensaje con información de errores
        let message = '';
        if (response.data.has_errors) {
          message = `⚠️ ATENCIÓN: ${response.data.vouchers_created}/${response.data.total_requested} fichas creadas\n\n`;
          message += `${response.data.vouchers_failed} fichas NO se pudieron crear en Mikrotik.\n\n`;
          if (response.data.errors && response.data.errors.length > 0) {
            message += `Errores:\n${response.data.errors.slice(0, 3).join('\n')}\n\n`;
          }
          if (generationType === 'seller') {
            message += `Nuevo saldo del vendedor: $${response.data.new_balance}`;
          }
        } else {
          message = generationType === 'admin' 
            ? `✅ ${response.data.vouchers_created} fichas generadas exitosamente\n\n⚠️ Estas fichas NO afectan ningún saldo.`
            : `✅ ${response.data.vouchers_created} fichas generadas exitosamente\n\nNuevo saldo del vendedor: $${response.data.new_balance}`;
        }
        
        alert(message);
        
        // FORZAR descarga de PDF si se creó al menos 1 ficha
        if (response.data.batch_id && response.data.vouchers_created > 0) {
          const size = printerType === 'thermal' ? 'ticket' : 'a4';
          
          // Usar async/await para esperar el PDF antes de continuar
          try {
            const pdfResponse = await voucherAPI.getPdfHtml(response.data.batch_id, size);
            const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            setLastPdfUrl(url);
            
            // FORZAR apertura del PDF (múltiples intentos)
            const newWindow = window.open(url, '_blank');
            
            // Si el navegador bloqueó, mostrar botón prominente
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
              alert('⚠️ El navegador bloqueó la ventana del PDF.\n\nHaz clic en el botón "Ver último PDF" para abrirlo.');
            }
          } catch (err) {
            console.error('Error descargando PDF:', err);
            alert('❌ Error al generar el PDF. Por favor, usa la opción "Reimprimir" para obtener las fichas.');
          }
        }
        
        // Limpiar form y volver a selección de tipo
        setQuantity(10);
        setSelectedPlan('');
        setSelectedRouter('');
        setSelectedSeller('');
        setGenerationType('');
        setShowTypeSelection(true);
      }
    } catch (error) {
      console.error('Error generando fichas:', error);
      alert('❌ Error generando fichas: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const loadRecentBatches = async (limit = batchLimit) => {
    try {
      const response = await api.get(`/api/admin/vouchers/recent-batches?limit=${limit}`); // ('/api/admin/vouchers/recent-batches');
      setRecentBatches(response.data);
    } catch (error) {
      console.error('Error cargando lotes:', error);
    }
  };

  const [selectedBatchVouchers, setSelectedBatchVouchers] = useState({}); // batchId -> array de vouchers
  const [selectedVoucherIds, setSelectedVoucherIds] = useState({}); // batchId -> Set de ids

  const toggleVoucherSelection = (batchId, voucherId) => {
    setSelectedVoucherIds((prev) => {
      const batchSelection = new Set(prev[batchId] || []);
      if (batchSelection.has(voucherId)) {
        batchSelection.delete(voucherId);
      } else {
        batchSelection.add(voucherId);
      }
      return { ...prev, [batchId]: Array.from(batchSelection) };
    });
  };

  const loadVouchersForBatch = async (batchId) => {
    try {
      const response = await voucherAPI.getByBatch(batchId);
      setSelectedBatchVouchers((prev) => ({ ...prev, [batchId]: response.data }));
      // Inicialmente ninguna ficha seleccionada
      setSelectedVoucherIds((prev) => ({ ...prev, [batchId]: [] }));
    } catch (error) {
      console.error('Error cargando fichas del lote:', error);
      alert('❌ No se pudieron cargar las fichas de este lote');
    }
  };

  const handleReprintBatch = async (batchId, size = 'a4', onlySelected = false) => {
    setVerifying(true);
    try {
      // PASO 1: Verificar si las fichas existen en Mikrotik ANTES de reimprimir
      let voucherIds = [];
      
      if (onlySelected) {
        voucherIds = selectedVoucherIds[batchId] || [];
        if (voucherIds.length === 0) {
          alert('Selecciona al menos una ficha para reimprimir.');
          setVerifying(false);
          return;
        }
      }
      
      // Verificar en Mikrotik
      const verifyPayload = onlySelected 
        ? { voucher_ids: voucherIds }
        : { batch_id: batchId };
      
      const verifyResponse = await api.post('/api/admin/vouchers/verify-batch', verifyPayload);
      
      // Si hay fichas faltantes, mostrar modal
      if (!verifyResponse.data.all_vouchers_valid) {
        setVerificationResult(verifyResponse.data);
        setCurrentBatchAction({ batchId, size, onlySelected, voucherIds });
        setShowRegenerateModal(true);
        setVerifying(false);
        return;
      }
      
      // Si todas existen, proceder a imprimir directamente
      await proceedToPrint(batchId, size, onlySelected);
      
    } catch (error) {
      console.error('Error verificando fichas:', error);
      // Si falla la verificación, preguntar si quiere imprimir de todos modos
      if (window.confirm('No se pudo verificar las fichas en Mikrotik. ¿Reimprimir de todos modos?')) {
        await proceedToPrint(batchId, size, onlySelected);
      }
    } finally {
      setVerifying(false);
    }
  };
  
  const proceedToPrint = async (batchId, size, onlySelected) => {
    try {
      let blob;
      if (onlySelected) {
        const ids = selectedVoucherIds[batchId] || [];
        const response = await voucherAPI.getCustomPdf(ids, size);
        blob = new Blob([response.data], { type: 'application/pdf' });
      } else {
        const response = await voucherAPI.getPdfHtml(batchId, size);
        blob = new Blob([response.data], { type: 'application/pdf' });
      }
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank');
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        alert('El navegador bloqueó la ventana emergente. Revisa el bloqueador de popups.');
      }
    } catch (error) {
      console.error('Error imprimiendo:', error);
      alert('❌ Error: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const handleRegenerateAndPrint = async (action) => {
    setRegenerating(true);
    try {
      const { batchId, size, onlySelected, voucherIds } = currentBatchAction;
      
      // Regenerar fichas faltantes
      if (action === 'regenerate' || action === 'both') {
        const missingIds = verificationResult.missing_vouchers.map(v => v.id);
        
        const regenerateResponse = await api.post('/api/admin/vouchers/regenerate', {
          voucher_ids: missingIds
        });
        
        const successMsg = `✅ ${regenerateResponse.data.results.regenerated}/${regenerateResponse.data.results.total} fichas regeneradas`;
        alert(successMsg);
      }
      
      // Imprimir
      if (action === 'print' || action === 'both') {
        await proceedToPrint(batchId, size, onlySelected);
      }
      
      setShowRegenerateModal(false);
      setVerificationResult(null);
      setCurrentBatchAction(null);
      
    } catch (error) {
      console.error('Error en acción:', error);
      alert('❌ Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRegenerating(false);
    }
  };

  const handleSearchVoucher = async () => {
    if (!searchCode.trim()) {
      alert('Ingresa un código de ficha');
      return;
    }

    setSearching(true);
    try {
      const response = await api.get(`/api/vouchers/search?code=${searchCode}`);
      setSearchResult(response.data);
    } catch (error) {
      console.error('Error buscando ficha:', error);
      setSearchResult(null);
      alert('Ficha no encontrada');
    } finally {
      setSearching(false);
    }
  };

  const handleCreateBinding = async () => {
    if (!selectedRouterBinding || !macAddress || !bindingComment) {
      alert('Por favor completa todos los campos');
      return;
    }

    setCreatingBinding(true);
    try {
      const response = await api.post('/api/admin/ip-bindings/create', {
        router_id: selectedRouterBinding,
        mac_address: macAddress,
        comment: bindingComment
      });

      if (response.data.success) {
        alert('✅ IP Binding creado exitosamente');
        setMacAddress('');
        setBindingComment('');
      }
    } catch (error) {
      console.error('Error creando binding:', error);
      alert('❌ Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingBinding(false);
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Header */}
      <header className={`shadow-sm transition-colors duration-300 ${isDark ? 'bg-gray-800 border-b border-gray-700' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-900'
              }`}
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <Ticket className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <div>
              <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Gestión de Fichas
              </h1>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Administración completa de vouchers y configuraciones
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className={`flex gap-2 mb-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setActiveTab('imprimir')}
            style={{
              color: activeTab === 'imprimir' 
                ? '#2563eb' 
                : isDark ? '#d1d5db' : '#4b5563'
            }}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'imprimir'
                ? 'border-blue-600'
                : 'border-transparent hover:opacity-80'
            }`}
          >
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                activeTab === 'imprimir'
                  ? 'bg-blue-100 text-blue-600'
                  : isDark
                    ? 'bg-gray-700 text-gray-300'
                    : 'bg-gray-100 text-gray-600'
              }`}>
                <Printer className="w-4 h-4" />
              </div>
              <span>Imprimir Fichas</span>
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('reimprimir')}
            style={{
              color: activeTab === 'reimprimir' 
                ? '#2563eb' 
                : isDark ? '#d1d5db' : '#4b5563'
            }}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'reimprimir'
                ? 'border-blue-600'
                : 'border-transparent hover:opacity-80'
            }`}
          >
            <div className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5" />
              Reimprimir Fichas
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('buscar')}
            style={{
              color: activeTab === 'buscar' 
                ? '#2563eb' 
                : isDark ? '#d1d5db' : '#4b5563'
            }}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'buscar'
                ? 'border-blue-600'
                : 'border-transparent hover:opacity-80'
            }`}
          >
            <div className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Buscar Ficha
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('bindings')}
            style={{
              color: activeTab === 'bindings' 
                ? '#2563eb' 
                : isDark ? '#d1d5db' : '#4b5563'
            }}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'bindings'
                ? 'border-blue-600'
                : 'border-transparent hover:opacity-80'
            }`}
          >
            <div className="flex items-center gap-2">
              <Wifi className="w-5 h-5" />
              IP Bindings
            </div>
          </button>
        </div>

        {/* Imprimir Fichas Tab */}
        {activeTab === 'imprimir' && (
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-8`}>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-6`}>
              Generar Nuevo Lote de Fichas
            </h2>

            {/* Selección de Tipo */}
            {showTypeSelection && !generationType && (
              <div className="space-y-6">
                <p className={`text-center ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
                  ¿Para quién deseas crear las fichas?
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Opción: Para Mí (Admin) */}
                  <button
                    onClick={() => {
                      setGenerationType('admin');
                      setShowTypeSelection(false);
                    }}
                    className={`p-8 rounded-xl border-2 transition-all hover:scale-105 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 hover:border-blue-500' 
                        : 'border-gray-300 bg-white hover:border-blue-600'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                        <Ticket className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Para Mí
                      </h3>
                      <p className={`text-sm text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Crear fichas para uso personal
                      </p>
                      <div className={`mt-2 px-4 py-2 rounded-lg ${isDark ? 'bg-green-900/30' : 'bg-green-50'}`}>
                        <p className="text-sm text-green-600 font-semibold">
                          ✓ No afecta ningún saldo
                        </p>
                      </div>
                    </div>
                  </button>

                  {/* Opción: Para Vendedor */}
                  <button
                    onClick={() => {
                      setGenerationType('seller');
                      setShowTypeSelection(false);
                    }}
                    className={`p-8 rounded-xl border-2 transition-all hover:scale-105 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 hover:border-blue-500' 
                        : 'border-gray-300 bg-white hover:border-blue-600'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                        <Users className="w-8 h-8 text-blue-600" />
                      </div>
                      <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Para Vendedor
                      </h3>
                      <p className={`text-sm text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Asignar fichas a un vendedor
                      </p>
                      <div className={`mt-2 px-4 py-2 rounded-lg ${isDark ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                        <p className="text-sm text-blue-600 font-semibold">
                          ⚠ Afecta saldo del vendedor
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Formulario para Admin */}
            {generationType === 'admin' && (
              <div className="space-y-6">
                {/* Botón Volver */}
                <button
                  onClick={() => {
                    setGenerationType('');
                    setShowTypeSelection(true);
                    setSelectedRouter('');
                    setSelectedPlan('');
                  }}
                  className={`text-sm ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900'} flex items-center gap-2`}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Cambiar tipo de generación
                </button>

                <div className={`p-4 rounded-lg ${isDark ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-200'}`}>
                  <p className={`text-sm ${isDark ? 'text-green-400' : 'text-green-700'} font-semibold`}>
                    ✓ Estas fichas NO afectarán ningún saldo
                  </p>
                </div>

                {/* Router */}
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Router *
                  </label>
                  <select
                    value={selectedRouter}
                    onChange={(e) => setSelectedRouter(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecciona un router</option>
                    {routers.map(router => (
                      <option key={router.id} value={router.id}>
                        {router.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Plan */}
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Plan *
                  </label>
                  <select
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    disabled={!selectedRouter}
                    className="w-full px-4 py-3 rounded-lg border bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <option value="">Selecciona un plan</option>
                    {plans.map(plan => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} - {plan.duration_formatted || 'N/A'}
                      </option>
                    ))}
                  </select>

                {/* Tipo de Impresora (Admin) */}
                <div className="mt-4">
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Tipo de Impresora *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => {
                        setPrinterType('normal');
                        setPdfSize('a4');
                      }}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        printerType === 'normal'
                          ? 'border-blue-600 bg-blue-50'
                          : isDark
                            ? 'border-gray-600 bg-gray-700'
                            : 'border-gray-300 bg-white'
                      }`}
                    >
                      <FileText className={`w-6 h-6 mx-auto mb-2 ${printerType === 'normal' ? 'text-blue-600' : isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                      <p className={`font-semibold ${printerType === 'normal' ? 'text-blue-600' : isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                        Normal (PDF A4)
                      </p>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Hasta 24 fichas por hoja
                      </p>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setPrinterType('thermal');
                        setPdfSize('ticket');
                      }}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        printerType === 'thermal'
                          ? 'border-blue-600 bg-blue-50'
                          : isDark
                            ? 'border-gray-600 bg-gray-700'
                            : 'border-gray-300 bg-white'
                      }`}
                    >
                      <Ticket className={`w-6 h-6 mx-auto mb-2 ${printerType === 'thermal' ? 'text-blue-600' : isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                      <p className={`font-semibold ${printerType === 'thermal' ? 'text-blue-600' : isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                        Térmica (Ticket)
                      </p>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        1 ficha por ticket
                      </p>
                    </button>
                  </div>
                </div>

                </div>

                {/* Cantidad */}
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Cantidad de Fichas *
                  </label>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                    min="1"
                    max="100"
                    className="w-full px-4 py-3 rounded-lg border bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500"
                  />
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Máximo 100 fichas por lote
                  </p>
                </div>

                {/* Botón para ver el último PDF generado si el popup fue bloqueado */}
                {lastPdfUrl && (
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() => window.open(lastPdfUrl, '_blank')}
                      className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      Ver último PDF generado
                    </button>
                  </div>
                )}

                {/* Botón Generar */}
                <button
                  onClick={handleGenerateVouchers}
                  disabled={generating || !selectedRouter || !selectedPlan}
                  className="w-full py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-semibold hover:from-green-700 hover:to-green-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {generating ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Generando Fichas...
                    </>
                  ) : (
                    <>
                      <Printer className="w-5 h-5" />
                      Generar Fichas Para Mí
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Formulario para Vendedor */}
            {generationType === 'seller' && (
              <div className="space-y-6">
                {/* Botón Volver */}
                <button
                  onClick={() => {
                    setGenerationType('');
                    setShowTypeSelection(true);
                    setSelectedRouter('');
                    setSelectedSeller('');
                    setSelectedPlan('');
                  }}
                  className={`text-sm ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900'} flex items-center gap-2`}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Cambiar tipo de generación
                </button>

                <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                  <p className={`text-sm ${isDark ? 'text-blue-400' : 'text-blue-700'} font-semibold`}>
                    ⚠ Estas fichas afectarán el saldo del vendedor seleccionado
                  </p>
                </div>

                {/* Router */}
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Router *
                  </label>
                  <select
                    value={selectedRouter}
                    onChange={(e) => setSelectedRouter(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecciona un router</option>
                    {routers.map(router => (
                      <option key={router.id} value={router.id}>
                        {router.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vendedor */}
                <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Vendedor *
                </label>
                <select
                  value={selectedSeller}
                  onChange={(e) => setSelectedSeller(e.target.value)}
                  disabled={!selectedRouter}
                  className="w-full px-4 py-3 rounded-lg border bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <option value="">Selecciona un vendedor</option>
                  {sellers.map(seller => (
                    <option key={seller.id} value={seller.id}>
                      {seller.name} ({seller.seller_type})
                    </option>
                  ))}
                </select>
              </div>

              {/* Plan */}
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Plan *
                </label>
                <select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  disabled={!selectedSeller || sellerPlans.length === 0}
                  className="w-full px-4 py-3 rounded-lg border bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <option value="">Selecciona un plan</option>
                  {sellerPlans.map(plan => (
                    <option key={plan.plan_id} value={plan.plan_id}>
                      {plan.plan_name} - ${plan.price} MXN
                    </option>
                  ))}
                </select>
              </div>

              {/* Tipo de Impresora */}
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Tipo de Impresora *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setPrinterType('normal')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      printerType === 'normal'
                        ? 'border-blue-600 bg-blue-50'
                        : isDark
                          ? 'border-gray-600 bg-gray-700'
                          : 'border-gray-300 bg-white'
                    }`}
                  >
                    <FileText className={`w-6 h-6 mx-auto mb-2 ${printerType === 'normal' ? 'text-blue-600' : isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                    <p className={`font-semibold ${printerType === 'normal' ? 'text-blue-600' : isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                      Normal (PDF)
                    </p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      21-24 fichas/hoja
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPrinterType('thermal')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      printerType === 'thermal'
                        ? 'border-blue-600 bg-blue-50'
                        : isDark
                          ? 'border-gray-600 bg-gray-700'
                          : 'border-gray-300 bg-white'
                    }`}
                  >
                    <Ticket className={`w-6 h-6 mx-auto mb-2 ${printerType === 'thermal' ? 'text-blue-600' : isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                    <p className={`font-semibold ${printerType === 'thermal' ? 'text-blue-600' : isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                      Térmica
                    </p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      1 ficha/ticket
                    </p>
                  </button>
                </div>
              </div>

              {/* Cantidad */}
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Cantidad de Fichas *
                </label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1"
                  className="w-full px-4 py-3 rounded-lg border bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Resumen */}
              {selectedPlan && sellerPlans.length > 0 && (
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}>
                  <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <strong>Total:</strong> {quantity} fichas × ${sellerPlans.find(p => p.plan_id === selectedPlan)?.price || 0} = 
                    <span className="text-blue-600 font-bold ml-2">
                      ${((sellerPlans.find(p => p.plan_id === selectedPlan)?.price || 0) * quantity).toFixed(2)} MXN
                    </span>
                  </p>
                </div>
              )}

              {/* Botón Generar */}
              <button
                onClick={handleGenerateVouchers}
                disabled={generating || !selectedRouter || !selectedSeller || !selectedPlan}
                className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {generating ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Generando Fichas...
                  </>
                ) : (
                  <>
                    <Printer className="w-5 h-5" />
                    Generar Fichas Para Vendedor
                  </>
                )}
              </button>
              </div>
            )}
          </div>
        )}

        {/* Reimprimir Tab */}
        {activeTab === 'reimprimir' && (
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-8`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Lotes Recientes
              </h2>
              
              <div className="flex items-center gap-2">
                <label className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Mostrar:
                </label>
                <select
                  value={batchLimit}
                  onChange={(e) => {
                    const newLimit = parseInt(e.target.value);
                    setBatchLimit(newLimit);
                    loadRecentBatches(newLimit);
                  }}
                  className={`px-3 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:ring-2 focus:ring-blue-500`}
                >
                  <option value={10}>10 lotes</option>
                  <option value={20}>20 lotes</option>
                  <option value={50}>50 lotes</option>
                  <option value={0}>Todos</option>
                </select>
              </div>
            </div>

            {recentBatches.length === 0 ? (
              <p className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                No hay lotes recientes
              </p>
            ) : (
              <div className="space-y-4">
                {recentBatches.map(batch => (
                  <div
                    key={batch.id}
                    className="p-4 rounded-lg border bg-white border-gray-200"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Lote #{batch.batch_number}
                        </p>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {batch.quantity} fichas - {batch.plan_name}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          {new Date(batch.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => handleReprintBatch(batch.id, 'a4')}
                          disabled={verifying}
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
                        >
                          <FileText className="w-4 h-4" />
                          PDF A4 (todas)
                        </button>
                        <button
                          onClick={() => handleReprintBatch(batch.id, 'ticket')}
                          disabled={verifying}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
                        >
                          <Ticket className="w-4 h-4" />
                          Ticket (todas)
                        </button>
                        <button
                          onClick={() => loadVouchersForBatch(batch.id)}
                          disabled={verifying}
                          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 text-sm"
                        >
                          <Search className="w-4 h-4" />
                          Ver fichas del lote
                        </button>
                      </div>
                    </div>

                    {/* Lista de fichas del lote con selección */}
                    {selectedBatchVouchers[batch.id] && (
                      <div className="mt-3 border-t border-gray-200 pt-3">
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-sm font-semibold text-gray-700">Fichas en este lote</p>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                const allIds = (selectedBatchVouchers[batch.id] || []).map(v => v.id);
                                setSelectedVoucherIds((prev) => ({ ...prev, [batch.id]: allIds }));
                              }}
                              className="px-3 py-1 text-xs rounded-lg border border-gray-300 hover:bg-gray-100"
                            >
                              Seleccionar todas
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedVoucherIds((prev) => ({ ...prev, [batch.id]: [] }));
                              }}
                              className="px-3 py-1 text-xs rounded-lg border border-gray-300 hover:bg-gray-100"
                            >
                              Limpiar selección
                            </button>
                          </div>
                        </div>

                        <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-lg">
                          <table className="min-w-full text-xs">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left">
                                  Seleccionar
                                </th>
                                <th className="px-3 py-2 text-left">Código</th>
                                <th className="px-3 py-2 text-left">Password</th>
                                <th className="px-3 py-2 text-left">Estado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedBatchVouchers[batch.id].map((voucher) => {
                                const currentSelection = selectedVoucherIds[batch.id] || [];
                                const isSelected = currentSelection.includes(voucher.id);
                                return (
                                  <tr key={voucher.id} className="border-t border-gray-100">
                                    <td className="px-3 py-2">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleVoucherSelection(batch.id, voucher.id)}
                                      />
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs">{voucher.code}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{voucher.password}</td>
                                    <td className="px-3 py-2 text-xs">
                                      {voucher.status || 'generated'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        <div className="mt-3 flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleReprintBatch(batch.id, 'a4', true)}
                            disabled={verifying}
                            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
                          >
                            <FileText className="w-4 h-4" />
                            Imprimir A4 (seleccionadas)
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReprintBatch(batch.id, 'ticket', true)}
                            disabled={verifying}
                            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm"
                          >
                            <Ticket className="w-4 h-4" />
                            Imprimir Ticket (seleccionadas)
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Buscar Tab */}
        {activeTab === 'buscar' && (
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-8`}>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-6`}>
              Buscar Ficha
            </h2>

            <div className="space-y-6">
              <div className="flex gap-4">
                <input
                  type="text"
                  value={searchCode}
                  onChange={(e) => setSearchCode(e.target.value)}
                  placeholder="Ingresa el código de la ficha"
                  className="flex-1 px-4 py-3 rounded-lg border bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchVoucher()}
                />
                <button
                  onClick={handleSearchVoucher}
                  disabled={searching || !searchCode.trim()}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <Search className="w-5 h-5" />
                  Buscar
                </button>
              </div>

              {searchResult && (
                <div className="p-6 rounded-lg border bg-white border-gray-300 shadow-md space-y-6">
                  {/* Encabezado */}
                  <h3 className="text-lg font-bold text-gray-900 pb-2 border-b border-gray-200 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Información de la Ficha
                  </h3>
                  
                  {/* Información Básica de DB */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      📋 Información Básica
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Código</p>
                        <p className="font-bold text-xl text-blue-600">{searchResult.code}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Password</p>
                        <p className="font-bold text-xl text-blue-600">{searchResult.password}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Plan</p>
                        <p className="font-semibold text-gray-900">{searchResult.plan_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Precio</p>
                        <p className="font-semibold text-gray-900">${searchResult.price || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Vendedor</p>
                        <p className="font-semibold text-gray-900">{searchResult.seller_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Punto de Venta</p>
                        <p className="font-semibold text-gray-900">{searchResult.pos_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Fecha de Creación</p>
                        <p className="font-semibold text-gray-900">
                          {searchResult.created_at ? new Date(searchResult.created_at).toLocaleDateString('es-MX') : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Hora de Creación</p>
                        <p className="font-semibold text-gray-900">
                          {searchResult.created_at ? new Date(searchResult.created_at).toLocaleTimeString('es-MX') : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Información en Tiempo Real de Mikrotik */}
                  {searchResult.real_time_data && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        🔴 Información en Tiempo Real (Mikrotik)
                      </h4>
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 space-y-3">
                        {/* Estado */}
                        <div className="flex items-center justify-between pb-2 border-b border-blue-200">
                          <span className="text-sm text-gray-600">Estado</span>
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            searchResult.is_active ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'
                          }`}>
                            {searchResult.is_active ? '🟢 ACTIVO' : '⚪ INACTIVO'}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-gray-600">Plan</p>
                            <p className="font-semibold text-gray-900">{searchResult.real_time_data.plan_name}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Tiempo de Uso</p>
                            <p className="font-semibold text-gray-900">{searchResult.real_time_data.uptime}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Datos Recibidos</p>
                            <p className="font-semibold text-gray-900">
                              {(parseInt(searchResult.real_time_data.bytes_in) / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Datos Enviados</p>
                            <p className="font-semibold text-gray-900">
                              {(parseInt(searchResult.real_time_data.bytes_out) / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                          {searchResult.real_time_data.start_date && (
                            <div>
                              <p className="text-xs text-gray-600">Fecha Inicio</p>
                              <p className="font-semibold text-gray-900">{searchResult.real_time_data.start_date}</p>
                            </div>
                          )}
                          {searchResult.real_time_data.end_date && (
                            <div>
                              <p className="text-xs text-gray-600">Fecha Fin</p>
                              <p className="font-semibold text-gray-900">{searchResult.real_time_data.end_date}</p>
                            </div>
                          )}
                        </div>

                        {/* Sesión Activa */}
                        {searchResult.real_time_data.active_session && (
                          <div className="mt-3 pt-3 border-t border-blue-200">
                            <p className="text-xs font-semibold text-gray-700 mb-2">📱 Sesión Activa</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <p className="text-xs text-gray-600">IP Asignada</p>
                                <p className="font-mono text-sm font-semibold text-gray-900">
                                  {searchResult.real_time_data.active_session.address}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600">Tiempo Inactivo</p>
                                <p className="text-sm font-semibold text-gray-900">
                                  {searchResult.real_time_data.active_session.idle_time}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* MAC Cookie */}
                        {searchResult.real_time_data.cookie && (
                          <div className="mt-3 pt-3 border-t border-blue-200">
                            <p className="text-xs font-semibold text-gray-700 mb-2">🍪 MAC Cookie</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <p className="text-xs text-gray-600">MAC Address</p>
                                <p className="font-mono text-sm font-semibold text-gray-900">
                                  {searchResult.real_time_data.cookie.mac_address}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600">Expira en</p>
                                <p className="text-sm font-semibold text-gray-900">
                                  {searchResult.real_time_data.cookie.expires_after}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Mensaje si no hay datos de Mikrotik */}
                  {!searchResult.real_time_data && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        ⚠️ No se pudo consultar información en tiempo real de Mikrotik
                      </p>
                    </div>
                  )}

                  {/* Botones de Acción */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => handleReprintBatch(searchResult.batch_id, 'a4')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
                    >
                      <FileText className="w-4 h-4" />
                      Imprimir A4
                    </button>
                    <button
                      type="button"
                      onClick={() => handleReprintBatch(searchResult.batch_id, 'ticket')}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm"
                    >
                      <Ticket className="w-4 h-4" />
                      Imprimir Ticket
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* IP Bindings Tab */}
        {activeTab === 'bindings' && (
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-8`}>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-6`}>
              Crear IP Binding
            </h2>

            <div className="space-y-6">
              {/* Router */}
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Router *
                </label>
                <select
                  value={selectedRouterBinding}
                  onChange={(e) => setSelectedRouterBinding(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecciona un router</option>
                  {routers.map(router => (
                    <option key={router.id} value={router.id}>
                      {router.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* MAC Address */}
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  MAC Address *
                </label>
                <input
                  type="text"
                  value={macAddress}
                  onChange={(e) => setMacAddress(e.target.value)}
                  placeholder="AA:BB:CC:DD:EE:FF"
                  className="w-full px-4 py-3 rounded-lg border bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Comentario */}
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Comentario *
                </label>
                <input
                  type="text"
                  value={bindingComment}
                  onChange={(e) => setBindingComment(e.target.value)}
                  placeholder="Ej: Cliente Juan Pérez"
                  className="w-full px-4 py-3 rounded-lg border bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Info */}
              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <strong>Configuración:</strong> Se creará un IP Binding con server=all, type=bypassed
                </p>
              </div>

              {/* Botón Crear */}
              <button
                onClick={handleCreateBinding}
                disabled={creatingBinding || !selectedRouterBinding || !macAddress || !bindingComment}
                className="w-full py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-semibold hover:from-green-700 hover:to-green-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {creatingBinding ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Creando Binding...
                  </>
                ) : (
                  <>
                    <Wifi className="w-5 h-5" />
                    Crear IP Binding
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Modal de Verificación y Regeneración */}
      {showRegenerateModal && verificationResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-6">
                <AlertCircle className="w-8 h-8 text-orange-500" />
                <div>
                  <h3 className="text-xl font-bold">Fichas Faltantes en Mikrotik</h3>
                  <p className="text-sm text-gray-500">
                    {verificationResult.missing} de {verificationResult.total} fichas NO están en el router
                  </p>
                </div>
              </div>

              {/* Lista de fichas faltantes */}
              <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4 mb-6 max-h-60 overflow-y-auto`}>
                <h4 className="font-semibold mb-3 text-red-600">⚠️ Fichas que NO existen en Mikrotik:</h4>
                <div className="space-y-2">
                  {verificationResult.missing_vouchers.map((v, idx) => (
                    <div key={idx} className={`${isDark ? 'bg-gray-600' : 'bg-white'} p-3 rounded-lg flex items-center space-x-3`}>
                      <XCircle className="w-5 h-5 text-red-500" />
                      <span className="font-mono font-bold">{v.code}</span>
                      <span className="text-sm text-gray-500">({v.status})</span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-sm mb-6 text-gray-600">
                Estas fichas están en la base de datos pero NO en el Mikrotik. ¿Qué deseas hacer?
              </p>

              {/* Opciones */}
              <div className="space-y-3">
                <button
                  onClick={() => handleRegenerateAndPrint('regenerate')}
                  disabled={regenerating}
                  className="w-full px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  <RefreshCw className={`w-5 h-5 ${regenerating ? 'animate-spin' : ''}`} />
                  <span>Solo Regenerar en Mikrotik</span>
                </button>

                <button
                  onClick={() => handleRegenerateAndPrint('print')}
                  disabled={regenerating}
                  className="w-full px-4 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  <Printer className="w-5 h-5" />
                  <span>Solo Reimprimir (fichas inválidas)</span>
                </button>

                <button
                  onClick={() => handleRegenerateAndPrint('both')}
                  disabled={regenerating}
                  className="w-full px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  <span>Regenerar + Reimprimir (recomendado)</span>
                </button>

                <button
                  onClick={() => {
                    setShowRegenerateModal(false);
                    setVerificationResult(null);
                    setCurrentBatchAction(null);
                  }}
                  disabled={regenerating}
                  className={`w-full px-4 py-3 rounded-lg transition-colors disabled:opacity-50 ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VouchersAdminPage;
